/* eslint-disable no-redeclare */
import React, { useCallback, useState } from 'react';

import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { toast } from 'react-toastify';
import { tournamentEngine } from 'tods-competition-factory';

import PenaltyDialog from 'components/dialogs/penaltyDialog/PenaltyDialog';
import { COMPETITION_ENGINE } from 'config/graphql/constants';
import { useSaveTournament } from 'hooks/tournament/useSaveTournament';
import { useTournamentContext } from 'hooks/tournament/useTournamentContext';
import type { MatchUpInterface } from 'typedefs/store/scheduleTypes';
import type { ParticipantInterface } from 'typedefs/store/tmxTypes';
import { participantsMapper } from 'utils/participant';

interface ModalProps {
  participants: ParticipantInterface[] | undefined;
  matchUp: MatchUpInterface | undefined;
}

const Modal = NiceModal.create(({ matchUp, participants }: ModalProps) => {
  const modal = useModal();
  const tournamentContext = useTournamentContext();

  const [saving, setSaving] = useState<boolean>();
  const { saveTournament } = useSaveTournament();
  const tournamentId = tournamentContext?.tournament?.tournamentId;

  const handleSaveSuspensionPoint = useCallback(
    // TODO: refactor BE to be able to use competition engine everywhere. Without this tournamentEngine.setTournamentId
    // the backend throws an error - can't add suspension points
    async (penaltyData: any) => {
      if (matchUp && matchUp.tournamentId !== tournamentId) {
        tournamentEngine.setTournamentId(matchUp.tournamentId);
      }

      setSaving(true);
      try {
        const saveTournamentResult = await saveTournament('saveSuspensionPoint', COMPETITION_ENGINE, penaltyData);
        if (saveTournamentResult?.data?.mutateTournament?.success) {
          toast.success('Suspension updated');
        }
        // TODO: check the comment above method declaration, this can potentially be removed once the mentioned thing is
        // figured out
        if (matchUp && matchUp.tournamentId !== tournamentId) {
          tournamentEngine.setTournamentId(tournamentId);
        }
      } finally {
        setSaving(false);
      }
    },
    [matchUp, saveTournament, tournamentId],
  );

  return (
    <PenaltyDialog
      loading={saving}
      open={modal.visible || saving}
      participants={participants}
      onClose={modal.hide}
      onExited={modal.remove}
      onSave={handleSaveSuspensionPoint}
      {...(matchUp && {
        matchUp,
        eventId: matchUp?.eventId,
        drawId: matchUp?.drawId,
        structureId: matchUp?.structureId,
      })}
    />
  );
});

function showPenaltyDialog({ matchUp }: { matchUp: MatchUpInterface }): void;
function showPenaltyDialog({
  participants,
}: {
  participants: ParticipantInterface[];
  matchUp?: MatchUpInterface;
}): void;
function showPenaltyDialog({
  matchUp,
  participants,
}: {
  matchUp?: MatchUpInterface;
  participants?: ParticipantInterface[];
}) {
  const mappedParticipants = participants ? participants : participantsMapper(matchUp?.sides ?? []);
  if (mappedParticipants.length) NiceModal.show(Modal, { matchUp, participants: mappedParticipants });
}

/**
 * Open a suspension points dialog for the chosen match up or participants
 */
export const usePenaltyDialog = () => useCallback(showPenaltyDialog, []);
